import { useCallback, useEffect, useState } from 'react';

import { window } from '../../../../globals';
import { useAsyncRequest } from '../../../../hooks';
import { get } from '../../../../redux/api';
import { selectOptionFactory } from '../../../../utils';
import { useProjectSettingsContext } from '../context';
import type { Jira } from '../../../../../shared/model-typings';
//  Note that we are explicitly using this Organization type, since the response includes the associations.
import type { Organization, SelectOption } from '../../../../types';

function useFetchOrganization(orgId: number) {
  //  We use useCallback here to ensure that the function is stable across renders.
  return useCallback(() => get<Organization>(`/api/organizations/${orgId}`), [orgId]);
}

const useJiras = () => {
  const [selectedJira, setSelectedJira] = useState<Partial<Jira> | null>(null);
  const organizationAsyncValue = useAsyncRequest(
    useFetchOrganization(window.user?.organizationId ?? 0),
  );

  //  After the organization is fetched, we can set the selected Jira.
  useEffect(() => {
    if (organizationAsyncValue.value?.jiras?.[0]) {
      setSelectedJira(organizationAsyncValue.value.jiras[0]);
    }
  }, [organizationAsyncValue.value]);

  return {
    isLoading: organizationAsyncValue.isLoading,
    errors: organizationAsyncValue.errors,
    jiras: organizationAsyncValue.value?.jiras ?? [],
    selectedJira,
    setSelectedJira,
  };
};

/**
 * This is the hook for the issue tracker settings page.
 * It pulls in the editable fields from the project context,
 * along with helpers for updating and saving the data.
 *
 * It also fetches other data which the page depends on and provides
 * it in the return value. Issue tracker settings depends on:
 * - Organization.jira (/api/organization/:id)
 */
const useProjectIssueTrackerSettings = () => {
  const { issueTracker } = useProjectSettingsContext();

  const jiras = useJiras();

  return {
    locator: issueTracker.locator,
    settings: issueTracker.settings,
    isDirty: issueTracker.isDirty,
    isSaving: issueTracker.isSaving,
    isSaveButtonDisabled: !issueTracker.isDirty ||
      issueTracker.isSaving ||
      jiras.isLoading,
    isLoading: jiras.isLoading,
    jiras: jiras.jiras,
    selectedJira: jiras.selectedJira,
    selectedJiraOption: jiras.selectedJira
      ? selectOptionFactory(jiras.selectedJira.id, jiras.selectedJira.name)
      : undefined,
    onSelectedJiraChange: useCallback((option: SelectOption<number>) =>
      jiras.setSelectedJira(jiras.jiras.find(jira => jira.id === option.value) ?? null), [jiras]),
    onSave: issueTracker.onSave,
    onUpdate: issueTracker.onUpdate,
    errors: [
      ...(issueTracker?.errors || []),
      ...(jiras?.errors || []),
    ],
    canUserEditSettings: issueTracker.canUserEditSettings,
    projectSettingsBaseURL: issueTracker.projectSettingsBaseURL,
  };
};

export default useProjectIssueTrackerSettings;
